/**
 * ConfigurableOutlinerView.tsx - Unified Outliner with Dynamic Field Configuration
 * 
 * CHANGE HISTORY:
 * - 251120_2127 danielbeer: Created unified outliner with dynamic field management
 * 
 * Features:
 * - Starts as single-field outliner
 * - Add/remove fields dynamically via UI
 * - Field mapping to external sources (FileMaker, JSON, etc.)
 * - Show/hide fields without deleting data
 * - Import/export field configurations
 * - All existing outliner functionality
 */

import React, { useState, useCallback, useMemo } from 'react';
import { MultiFieldOutlinerView, type MultiFieldNode, type FieldDefinition } from './MultiFieldOutlinerView';
import { OutlinerView, type OutlinerNode } from './OutlinerView';
import '../styles/configurable-outliner.css';

export interface FieldMapping {
  outlinerFieldId: string;
  externalFieldName: string;
  direction: 'import' | 'export' | 'both';
  transform?: (value: any) => any;
}

export interface ExternalSchema {
  type: 'filemaker' | 'json' | 'csv' | 'custom';
  fields: Array<{
    name: string;
    type: string;
    label?: string;
  }>;
  layoutName?: string;  // For FileMaker
  tableName?: string;   // For FileMaker
}

export interface ConfigurableOutlinerViewProps {
  nodes: OutlinerNode[] | MultiFieldNode[];
  onChange?: (nodes: OutlinerNode[] | MultiFieldNode[]) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  
  // Field configuration
  initialFields?: FieldDefinition[];
  onFieldsChange?: (fields: FieldDefinition[]) => void;
  
  // Field mapping
  externalSchema?: ExternalSchema;
  fieldMappings?: FieldMapping[];
  onFieldMappingsChange?: (mappings: FieldMapping[]) => void;
  
  // UI options
  showFieldManager?: boolean;
  showFieldMapper?: boolean;
  allowFieldCreation?: boolean;
  
  // Standard outliner props
  familyRules?: boolean;
  className?: string;
  viewId?: string;
}

export const ConfigurableOutlinerView: React.FC<ConfigurableOutlinerViewProps> = ({
  nodes: initialNodes,
  onChange,
  onSelectionChange,
  initialFields = [],
  onFieldsChange,
  externalSchema,
  fieldMappings = [],
  onFieldMappingsChange,
  showFieldManager = true,
  showFieldMapper = false,
  allowFieldCreation = true,
  familyRules = true,
  className = '',
  viewId = 'configurable-outliner'
}) => {
  // Field definitions (starts with just 'text' field if none provided)
  const [fields, setFields] = useState<FieldDefinition[]>(
    initialFields.length > 0 ? initialFields : [
      {
        id: 'text',
        label: 'Text',
        width: 400,
        type: 'text',
        editable: true,
        resizable: true,
        placeholder: 'Enter text...'
      }
    ]
  );
  
  // Active field mappings
  const [mappings, setMappings] = useState<FieldMapping[]>(fieldMappings);
  
  // UI state
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [showMappingConfig, setShowMappingConfig] = useState(false);
  const [editingField, setEditingField] = useState<FieldDefinition | null>(null);
  
  // Convert nodes to multi-field format
  const multiFieldNodes = useMemo<MultiFieldNode[]>(() => {
    return initialNodes.map(node => {
      // If already multi-field, return as-is
      if ('fields' in node && node.fields) {
        return node as MultiFieldNode;
      }
      
      // Convert single-field to multi-field
      const singleFieldNode = node as OutlinerNode;
      return {
        ...singleFieldNode,
        fields: {
          text: singleFieldNode.text,
          // Initialize other fields as empty
          ...fields.reduce((acc, field) => {
            if (field.id !== 'text') {
              acc[field.id] = '';
            }
            return acc;
          }, {} as Record<string, string>)
        }
      };
    });
  }, [initialNodes, fields]);
  
  // Handle field changes
  const handleFieldsChange = useCallback((newFields: FieldDefinition[]) => {
    setFields(newFields);
    onFieldsChange?.(newFields);
  }, [onFieldsChange]);
  
  // Add new field
  const addField = useCallback(() => {
    const newField: FieldDefinition = {
      id: `field-${Date.now()}`,
      label: 'New Field',
      width: 150,
      type: 'text',
      editable: true,
      resizable: true,
      placeholder: ''
    };
    
    setEditingField(newField);
    setShowFieldConfig(true);
  }, []);
  
  // Save field (add or update)
  const saveField = useCallback((field: FieldDefinition) => {
    setFields(prev => {
      const existing = prev.find(f => f.id === field.id);
      if (existing) {
        return prev.map(f => f.id === field.id ? field : f);
      } else {
        return [...prev, field];
      }
    });
    
    setEditingField(null);
    setShowFieldConfig(false);
  }, []);
  
  // Remove field
  const removeField = useCallback((fieldId: string) => {
    if (fieldId === 'text') {
      alert('Cannot remove the primary text field');
      return;
    }
    
    if (confirm('Remove this field? Data will be preserved but hidden.')) {
      setFields(prev => prev.filter(f => f.id !== fieldId));
    }
  }, []);
  
  // Toggle field visibility
  const toggleFieldVisibility = useCallback((fieldId: string) => {
    setFields(prev => prev.map(f => 
      f.id === fieldId 
        ? { ...f, visible: !(f as any).visible !== false }
        : f
    ));
  }, []);
  
  // Import field mapping from external schema
  const importFieldMapping = useCallback((externalFieldName: string, outlinerFieldId: string) => {
    const newMapping: FieldMapping = {
      outlinerFieldId,
      externalFieldName,
      direction: 'both'
    };
    
    setMappings(prev => {
      const existing = prev.find(m => m.outlinerFieldId === outlinerFieldId);
      if (existing) {
        return prev.map(m => m.outlinerFieldId === outlinerFieldId ? newMapping : m);
      }
      return [...prev, newMapping];
    });
    
    onFieldMappingsChange?.([...mappings, newMapping]);
  }, [mappings, onFieldMappingsChange]);
  
  // Apply mappings when importing data
  const applyImportMappings = useCallback((externalData: any[]): MultiFieldNode[] => {
    return externalData.map((item, index) => {
      const fields: Record<string, string> = {};
      
      // Apply each mapping
      mappings.forEach(mapping => {
        if (mapping.direction === 'import' || mapping.direction === 'both') {
          const value = item[mapping.externalFieldName];
          fields[mapping.outlinerFieldId] = mapping.transform 
            ? mapping.transform(value)
            : String(value || '');
        }
      });
      
      return {
        id: `imported-${index}`,
        text: fields.text || '',
        fields,
        parentId: null,
        generation: 0
      };
    });
  }, [mappings]);
  
  // Apply mappings when exporting data
  const applyExportMappings = useCallback((nodes: MultiFieldNode[]): any[] => {
    return nodes.map(node => {
      const exported: Record<string, any> = {};
      
      mappings.forEach(mapping => {
        if (mapping.direction === 'export' || mapping.direction === 'both') {
          const value = node.fields[mapping.outlinerFieldId];
          exported[mapping.externalFieldName] = mapping.transform
            ? mapping.transform(value)
            : value;
        }
      });
      
      return exported;
    });
  }, [mappings]);
  
  // Render field configuration panel
  const renderFieldConfig = () => {
    if (!showFieldConfig) return null;
    
    return (
      <div className="field-config-overlay">
        <div className="field-config-panel">
          <h3>{editingField ? 'Edit Field' : 'Add Field'}</h3>
          
          <div className="field-config-form">
            <label>
              Field ID:
              <input
                type="text"
                value={editingField?.id || ''}
                onChange={(e) => setEditingField(prev => prev ? { ...prev, id: e.target.value } : null)}
                disabled={!!fields.find(f => f.id === editingField?.id)}
              />
            </label>
            
            <label>
              Label:
              <input
                type="text"
                value={editingField?.label || ''}
                onChange={(e) => setEditingField(prev => prev ? { ...prev, label: e.target.value } : null)}
              />
            </label>
            
            <label>
              Type:
              <select
                value={editingField?.type || 'text'}
                onChange={(e) => setEditingField(prev => prev ? { ...prev, type: e.target.value as any } : null)}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="select">Select</option>
              </select>
            </label>
            
            <label>
              Width (px):
              <input
                type="number"
                value={editingField?.width || 150}
                onChange={(e) => setEditingField(prev => prev ? { ...prev, width: parseInt(e.target.value) } : null)}
              />
            </label>
            
            {editingField?.type === 'select' && (
              <label>
                Options (comma-separated):
                <input
                  type="text"
                  value={editingField?.options?.join(', ') || ''}
                  onChange={(e) => setEditingField(prev => prev ? { 
                    ...prev, 
                    options: e.target.value.split(',').map(s => s.trim())
                  } : null)}
                />
              </label>
            )}
            
            <label>
              Placeholder:
              <input
                type="text"
                value={editingField?.placeholder || ''}
                onChange={(e) => setEditingField(prev => prev ? { ...prev, placeholder: e.target.value } : null)}
              />
            </label>
            
            <div className="field-config-actions">
              <button onClick={() => editingField && saveField(editingField)}>Save</button>
              <button onClick={() => { setEditingField(null); setShowFieldConfig(false); }}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render field mapper panel
  const renderFieldMapper = () => {
    if (!showMappingConfig || !externalSchema) return null;
    
    return (
      <div className="field-mapper-overlay">
        <div className="field-mapper-panel">
          <h3>Map Fields: {externalSchema.layoutName || externalSchema.tableName || 'External Source'}</h3>
          
          <div className="field-mapper-grid">
            <div className="mapper-column">
              <h4>Outliner Fields</h4>
              {fields.map(field => (
                <div key={field.id} className="mapper-field">
                  {field.label}
                </div>
              ))}
            </div>
            
            <div className="mapper-column">
              <h4>↔</h4>
            </div>
            
            <div className="mapper-column">
              <h4>{externalSchema.type === 'filemaker' ? 'FileMaker Fields' : 'External Fields'}</h4>
              {externalSchema.fields.map(extField => (
                <div key={extField.name} className="mapper-field">
                  <select
                    value={mappings.find(m => m.externalFieldName === extField.name)?.outlinerFieldId || ''}
                    onChange={(e) => importFieldMapping(extField.name, e.target.value)}
                  >
                    <option value="">-- Not Mapped --</option>
                    {fields.map(field => (
                      <option key={field.id} value={field.id}>{field.label}</option>
                    ))}
                  </select>
                  <span>{extField.label || extField.name}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="field-mapper-actions">
            <button onClick={() => setShowMappingConfig(false)}>Close</button>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className={`configurable-outliner-container ${className}`}>
      {/* Toolbar */}
      {showFieldManager && (
        <div className="outliner-toolbar">
          <div className="toolbar-section">
            <span className="toolbar-label">Fields:</span>
            {fields.map(field => (
              <button
                key={field.id}
                className="field-chip"
                onClick={() => {
                  setEditingField(field);
                  setShowFieldConfig(true);
                }}
                title={`Edit ${field.label}`}
              >
                {field.label}
                {field.id !== 'text' && (
                  <span 
                    className="field-chip-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeField(field.id);
                    }}
                  >
                    ×
                  </span>
                )}
              </button>
            ))}
            
            {allowFieldCreation && (
              <button className="add-field-btn" onClick={addField}>
                + Add Field
              </button>
            )}
          </div>
          
          {showFieldMapper && externalSchema && (
            <div className="toolbar-section">
              <button 
                className="map-fields-btn"
                onClick={() => setShowMappingConfig(true)}
              >
                🔗 Map Fields
              </button>
              <span className="mapping-count">
                {mappings.length} mapped
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Outliner */}
      <div className="outliner-content">
        <MultiFieldOutlinerView
          nodes={multiFieldNodes}
          fieldDefinitions={fields}
          onChange={onChange}
          onSelectionChange={onSelectionChange}
          onFieldDefinitionsChange={handleFieldsChange}
          familyRules={familyRules}
          showHeader={fields.length > 1}
          viewId={viewId}
        />
      </div>
      
      {/* Configuration Panels */}
      {renderFieldConfig()}
      {renderFieldMapper()}
    </div>
  );
};
